// Экран раздела: разбирает адрес /:section/:view, проверяет право и отдаёт модуль.
// Прямая ссылка на раздел без права — не «доступ запрещён», а объяснение с шагом
// (правило 10). Скрытие раздела в полосе — удобство навигации, изоляция данных живёт
// на сервере выборкой.

import { useEffect } from 'react';

import { RiLockLine, RiQuestionLine } from '@remixicon/react';
import { Link, useParams } from 'react-router';

import { sectionBySlug } from '@/app/sections';
import { CasesSection } from '@/modules/cases/CasesSection';
import { HiringSection } from '@/modules/hiring/HiringSection';
import { MoneySection } from '@/modules/money/MoneySection';
import { PayrollSection } from '@/modules/payroll/PayrollSection';
import { PeopleSection } from '@/modules/people/PeopleSection';
import { SettingsSection } from '@/modules/settings/SettingsSection';
import { SitesSection } from '@/modules/sites/SitesSection';
import { useAuthStore } from '@/shared/auth/store';
import { canAny, defaultViewFor } from '@/shared/rbac/permissions';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState } from '@/shared/ui/screen-state';

export function SectionScreen() {
  const params = useParams<{ section: string; view?: string; rowId?: string }>();
  const permissions = useAuthStore((state) => state.permissions);

  const section = params.section === undefined ? undefined : sectionBySlug(params.section);

  // Заголовок окна называет раздел.
  //
  // Программа поставляется десктопной обёрткой (решение 7), и заголовок окна — это то, что
  // человек видит в панели задач Windows, когда переключается между окнами. До 04.09.2026 он
  // был статическим «Барк»: два открытых окна программы, ведомость и реестр людей, различались
  // в панели задач НИЧЕМ. В браузере то же самое с вкладками.
  //
  // Раздел, а не представление: представление меняется чаще, чем человек смотрит на панель
  // задач, а мигающий заголовок читается хуже устойчивого.
  useEffect(() => {
    document.title = section === undefined ? 'Барк' : `${section.title} — Барк`;
  }, [section]);

  if (section === undefined) {
    return (
      <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>
        <EmptyState
          icon={RiQuestionLine}
          // Кусок адреса в заголовке не показывается. До 25.08.2026 здесь стояло
          // «Раздела “tasks” в системе нет»: латиницей, служебным словом, которого нет ни в
          // одном экране и ни в одной кнопке. Человеку это не говорит ничего — он видел
          // название по-русски или вовсе пришёл по ссылке из переписки.
          title='Такого раздела в системе нет'
          description='Ссылка устарела или в ней опечатка. Разделы системы перечислены слева.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to='/'>На стартовый экран</Link>
            </LinkButton.Root>
          }
        />
      </main>
    );
  }

  if (!canAny(permissions, section.permissions)) {
    return (
      <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>
        <EmptyState
          icon={RiLockLine}
          title={`Раздел «${section.title}» не входит в ваши права`}
          description='Права выдаёт администратор системы. Если раздел нужен для работы — попросите расширить права и войдите заново.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to='/'>На стартовый экран</Link>
            </LinkButton.Root>
          }
        />
      </main>
    );
  }

  const view = params.view ?? defaultViewFor(section, permissions);

  switch (section.slug) {
    case 'money':
      return <MoneySection view={view} />;
    case 'people':
      return <PeopleSection view={view} />;
    case 'sites':
      return <SitesSection view={view} />;
    case 'payroll':
      return <PayrollSection view={view} />;
    case 'cases':
      return <CasesSection />;
    case 'settings':
      return <SettingsSection view={view} />;
    case 'hiring':
      return <HiringSection view={view} />;
  }
}
